import { Box, Icon, Typography } from "@outschool/backpack";
import { AddToCollectionModal_ActivityFragment } from "@outschool/gql-frontend-generated";
import { fasHeart } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import {
  Checkbox,
  Modal,
  ModalActionButtons,
  Space,
} from "@outschool/ui-legacy-component-library";
import { fromEntries } from "@outschool/ui-utils";
import difference from "lodash/difference";
import React from "react";

import CreateNewCollectionModal from "./CreateNewCollectionModal";
import { useListCurrentUserSavedClassLists } from "./graphql/hooks";
import {
  useSaveToCollections,
  useSavedActivityMutations,
  useUnsaveToCollections,
} from "./graphql/mutations";

export interface AddToCollectionModalProps {
  /**
   * What mode the modal is in.
   * "closed" means the modal is hidden
   * "add" means the activity was just saved and we are giving the user an open to add to a collection
   * "edit" means the activity was previously saved to a list and we are editing the collections saved
   */
  mode: "closed" | "add" | "edit";
  /**
   * Called when the "Done" button is clicked
   */
  onDone: () => void;
  /**
   * What activity we are adding to a collection
   */
  activity: AddToCollectionModal_ActivityFragment;
}

export default function AddToCollectionModal({
  mode,
  onDone,
  activity,
}: AddToCollectionModalProps) {
  const { t } = useTranslation(
    "client\\components\\saving\\AddToCollectionModal",
  );
  const saveToCollections = useSaveToCollections(activity.uid);
  const unsaveToCollections = useUnsaveToCollections(activity.uid);
  const {
    unsaveActivity: [unsaveActivity],
  } = useSavedActivityMutations(activity?.uid);
  const currentlySavedClassListUids = React.useMemo(
    () => activity.savedClassLists?.map(scl => scl.uid) ?? [],
    [activity.savedClassLists],
  );
  const { loading, savedClassLists } = useListCurrentUserSavedClassLists();
  const savedClassListsSorted = React.useMemo(() => {
    return [...savedClassLists].sort((a, b) => {
      return (
        new Date(b.lastAddedToAt).getTime() -
        new Date(a.lastAddedToAt).getTime()
      );
    });
  }, [savedClassLists]);
  const [selectedSavedClassLists, setSelectedSavedClassLists] = React.useState(
    typeof Object?.fromEntries === "function"
      ? Object.fromEntries(currentlySavedClassListUids.map(uid => [uid, true]))
      : fromEntries(currentlySavedClassListUids.map(uid => [uid, true])),
  );
  const [isFavoritesChecked, setIsFavoritesChecked] = React.useState(true);

  const [isCreateCollectionModalOpen, setIsCreateCollectionModalOpen] =
    React.useState(false);
  const [savingInProgress, setSavingInProgress] = React.useState(false);

  const onDoneClick = React.useCallback(async () => {
    setSavingInProgress(true);
    try {
      if (!isFavoritesChecked) {
        await unsaveActivity();
      } else {
        const selectedUids = Object.entries(selectedSavedClassLists)
          .filter(x => x[1])
          .map(x => x[0]);
        const uidsToSave = difference(
          selectedUids,
          currentlySavedClassListUids,
        );
        const uidsToUnsave = difference(
          currentlySavedClassListUids,
          selectedUids,
        );
        await Promise.all([
          saveToCollections(uidsToSave),
          unsaveToCollections(uidsToUnsave),
        ]);
      }
      onDone();
    } finally {
      setSavingInProgress(false);
    }
  }, [
    isFavoritesChecked,
    unsaveActivity,
    saveToCollections,
    unsaveToCollections,
    selectedSavedClassLists,
    onDone,
    currentlySavedClassListUids,
  ]);

  if (loading) {
    return null;
  }

  return (
    <>
      <CreateNewCollectionModal
        activityUid={activity.uid}
        open={isCreateCollectionModalOpen}
        onCancel={() => setIsCreateCollectionModalOpen(false)}
        onCreated={() => {
          setIsCreateCollectionModalOpen(false);
          onDone();
        }}
      />
      <Modal open={mode !== "closed"} onClose={onDone}>
        <Typography variant="h3">{t`Add to lists`}</Typography>
        <Box
          sx={{
            maxHeight: "285px",
            overflow: "auto",
          }}
        >
          <SelectableCollectionRow
            icon={
              <Icon
                icon={fasHeart}
                color="error"
                sx={{
                  marginRight: "1em",
                }}
              />
            }
            name={t`Favorites`}
            checked={isFavoritesChecked}
            disabled={mode === "add"}
            onClick={() => {
              setIsFavoritesChecked(!isFavoritesChecked);
              if (isFavoritesChecked) {
                setSelectedSavedClassLists({});
              }
            }}
          />
          {savedClassListsSorted.map(savedClassList => (
            <SelectableCollectionRow
              key={savedClassList.uid}
              icon={<></>}
              name={savedClassList.name}
              disabled={!isFavoritesChecked}
              checked={!!selectedSavedClassLists[savedClassList.uid]}
              onClick={() => {
                const newValue = !selectedSavedClassLists[savedClassList.uid];
                if (newValue && !isFavoritesChecked) {
                  setIsFavoritesChecked(true);
                }
                setSelectedSavedClassLists({
                  ...selectedSavedClassLists,
                  [savedClassList.uid]: newValue,
                });
              }}
            />
          ))}
        </Box>
        <Space y="medium" />
        <ModalActionButtons
          cancelLabel={t`Create new list`}
          onCancel={() => {
            setIsCreateCollectionModalOpen(true);
          }}
          primaryLabel={t`Done`}
          onPrimary={onDoneClick}
          disabled={savingInProgress}
        />
      </Modal>
    </>
  );
}

function SelectableCollectionRow({
  icon,
  name,
  disabled,
  checked,
  onClick,
}: {
  icon: React.ReactElement;
  name: string;
  disabled?: boolean;
  checked: boolean;
  onClick: React.MouseEventHandler;
}) {
  const wrappedOnClick: React.MouseEventHandler = React.useCallback(
    e => {
      // stop propogation and prevent default to stop double 'onClick' events
      e.stopPropagation();
      e.preventDefault();
      onClick(e);
    },
    [onClick],
  );
  return (
    <Box
      flex
      onClick={wrappedOnClick}
      sx={{
        alignItems: "center",
        padding: "1em",
        borderRadius: "0.25em",
        marginBottom: "0.5em",
        ...(checked
          ? {
              backgroundColor: "primary.100",
            }
          : {
              "&:hover": {
                backgroundColor: "grey.100",
              },
            }),
      }}
    >
      {icon}
      {name}
      <Checkbox
        checked={checked}
        disabled={disabled}
        sx={{ marginLeft: "auto" }}
      />
    </Box>
  );
}
